/**
 * @fileoverview Home.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import Header from '../components/layout/Header'
import SriLankaSection from '../components/home/SriLankaSection'
import TourSection from '../components/tours/TourSection'
import WhenToTravelSection from '../components/home/WhenToTravelSection'
import SEO from '../components/ui/SEO'
import { useTranslation } from 'react-i18next'

function WhyKithTravels() {
  const { t } = useTranslation();
  return (
    <div className='w-full bg-gray-50 px-[5%] md:px-[10%] py-20 flex flex-col gap-12 text-left'>
      <div className='flex flex-col gap-4 max-w-2xl'>
        <span className='text-KITH-primary font-bold tracking-widest uppercase text-xs'>{t('home.why_choose.subtitle')}</span>
        <h2 className='text-2xl md:text-4xl font-bold text-gray-800 leading-tight'>
          {t('home.why_choose.title1')} <span className='text-KITH-primary'>Sri Lanka</span>
        </h2>
        <p className='text-gray-500 text-sm md:text-base leading-relaxed'>
          {t('home.why_choose.desc')}
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
        {[
          {
            icon: "fi fi-br-diamond",
            title: t('home.why_choose.f1_title'),
            desc: t('home.why_choose.f1_desc')
          },
          {
            icon: "fi fi-br-map-marker",
            title: t('home.why_choose.f2_title'),
            desc: t('home.why_choose.f2_desc')
          },
          {
            icon: "fi fi-br-headset",
            title: t('home.why_choose.f3_title'),
            desc: t('home.why_choose.f3_desc')
          },
          {
            icon: "fi fi-br-users",
            title: t('home.why_choose.f4_title'),
            desc: t('home.why_choose.f4_desc')
          }
        ].map((item, idx) => (
          <div key={idx} className='bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col gap-4'>
            <div className='w-12 h-12 rounded-2xl bg-KITH-primary/10 flex items-center justify-center text-KITH-primary text-xl'>
              <i className={item.icon}></i>
            </div>
            <h3 className='text-lg font-bold text-gray-800 leading-tight'>{item.title}</h3>
            <p className='text-gray-500 text-xs md:text-sm leading-relaxed'>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Testimonials() {
  const testimonials = [
    {
      name: "Chantal & Pierre",
      country: "France",
      flag: "🇫🇷",
      text: "Our 10-day tour of Sri Lanka with KITH Travels was exceptional. The itinerary beautifully balanced culture in Sigiriya with relaxation in Mirissa. Our guide was incredibly knowledgeable and caring.",
      rating: 5
    },
    {
      name: "David L.",
      country: "United Kingdom",
      flag: "🇬🇧",
      text: "Flawless organization! From the comfortable A/C car to the amazing boutique hotels, everything exceeded our expectations. Truly a professional tour operator in Sri Lanka.",
      rating: 5
    },
    {
      name: "Yuki Tanaka",
      country: "Japan",
      flag: "🇯🇵",
      text: "KITH Travels customized a beautiful cycling and tea estate tour for us. Extremely fast responses and top-notch safety measures throughout. Highly recommended!",
      rating: 5
    },
    {
      name: "Sarah & Mark",
      country: "Australia",
      flag: "🇦🇺",
      text: "An unforgettable safari and beach holiday. The staff went above and beyond to accommodate our kids' preferences. We can't wait to return to Sri Lanka with KITH!",
      rating: 5
    }
  ];

  return (
    <div className='w-full bg-gray-50 px-[5%] md:px-[10%] py-24 flex flex-col gap-12 text-left overflow-hidden'>
      <div className='flex flex-col gap-4 max-w-2xl'>
        <span className='text-KITH-primary font-bold tracking-widest uppercase text-xs'>Testimonials</span>
        <h2 className='text-2xl md:text-4xl font-bold text-gray-800 leading-tight'>
          Loved by Travelers <span className='text-KITH-primary'>Worldwide</span>
        </h2>
        <p className='text-gray-500 text-sm md:text-base leading-relaxed'>
          Hear about the authentic Sri Lankan experiences and personalized journeys of our guests.
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
        {testimonials.map((item, idx) => (
          <div key={idx} className='bg-white p-8 rounded-3xl border border-gray-100 shadow-sm flex flex-col justify-between gap-6 hover:shadow-lg transition-all duration-300'>
            <div className='flex flex-col gap-4'>
              <div className='flex gap-1 text-amber-500'>
                {Array.from({ length: item.rating }).map((_, i) => (
                  <i key={i} className="fi fi-sr-star text-sm"></i>
                ))}
              </div>
              <p className='text-gray-600 text-xs md:text-sm leading-relaxed italic'>
                "{item.text}"
              </p>
            </div>
            <div className='flex items-center gap-3 border-t border-gray-50 pt-4'>
              <div className='text-2xl'>{item.flag}</div>
              <div className='flex flex-col'>
                <span className='font-bold text-gray-800 text-sm'>{item.name}</span>
                <span className='text-[10px] text-gray-400 font-semibold uppercase tracking-wider'>{item.country}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoogleMapEmbed() {
  const { t } = useTranslation();
  return (
    <div className='w-full px-[5%] md:px-[10%] py-16 bg-white flex flex-col gap-8 text-left'>
      <div className='flex flex-col gap-3'>
        <span className='text-KITH-primary font-bold tracking-widest uppercase text-xs'>{t('home.location.subtitle')}</span>
        <h3 className='text-xl md:text-2xl font-bold text-gray-800'>{t('home.location.title')}</h3>
        <p className='text-gray-500 text-xs md:text-sm'>
          {t('home.location.desc')}
        </p>
      </div>
      <div className='w-full h-[350px] md:h-[450px] rounded-3xl overflow-hidden shadow-md border border-gray-100'>
        <iframe 
          title="KITH Travels Location Dondangoda"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31697.551694200424!2d80.00762961520692!3d6.586111874213123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae22e118320bbcb%3A0x868cb5a3d7e5d800!2sDodangoda%2C%20Sri%20Lanka!5e0!3m2!1sen!2slk!4v1716300000000!5m2!1sen!2slk" 
          width="100%" 
          height="100%" 
          style={{ border: 0 }} 
          allowFullScreen="" 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  );
}

function Home() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": "KITH Travels Sri Lanka",
    "image": "https://kithtravels.com/thumbnail.jpg",
    "description": "Experience bespoke luxury travel in Sri Lanka with KITH Travels. We curate exclusive, tailor-made itineraries featuring premium accommodations, expert local guides, and unparalleled 24/7 concierge service.",
    "url": "https://kithtravels.com",
    "telephone": "+94703272573",
    "email": "info@kithtravels.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "No.295, 6th Lane, Pilomina Estate, Dondangoda",
      "addressLocality": "Dondangoda",
      "addressCountry": "LK"
    }
  };

  return (
    <div className='w-full'>
      <SEO 
        title="Visit Sri Lanka | Premier Sri Lankan DMC" 
        schema={schemaData}
        description="Travel Sri Lanka with KITH Travels, your trusted Sri Lankan DMC. Discover bespoke tour plans, authentic cultural experiences, and thrilling Sri Lankan wildlife safaris. Start planning your unforgettable vacation in Sri Lanka today."
        keywords="Travel Sri Lanka, Visit Sri Lanka, Sri Lankan DMC, Vacation in Sri Lanka, Sri Lankan wildlife, Sri Lanka travel agency, bespoke tours, Sri Lanka vacations"
      />
        <Header/>
        <div className=''>
          <SriLankaSection/>
          <TourSection />
          <WhenToTravelSection />
          <WhyKithTravels />
          <GoogleMapEmbed />
        </div>
    </div>
  )
}

export default Home